import { apiClient, apiClientPaginated, apiBlob, ApiError } from '@/lib/api-client';
import { exportQuery } from '@/lib/download-file';
import type {
  CancelSalesOrderPayload,
  CreateDeliveryPayload,
  CreateInvoicePayload,
  RecordSalesPaymentPayload,
  SalesOrder,
  SalesOrderDelivery,
  SalesOrderDocumentPayload,
  SalesOrderInvoice,
  SalesOrderListItem,
  SalesOrderListParams,
  SalesOrderListResponse,
  SalesOrderPayment,
  SalesOrderStats,
  SalesOrderStatsParams,
} from './types';

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_STATS: SalesOrderStats = {
  open_orders: 0, pending_delivery_value: 0, invoiced_total: 0, outstanding: 0, advances: 0,
};

export const salesOrdersApi = {
  // No fallback: a dead endpoint must not masquerade as an empty list.
  list: (params: SalesOrderListParams) =>
    apiClientPaginated<SalesOrderListItem[]>(
      `/sales-orders${buildQuery({
        page: params.page, limit: params.limit, status: params.status || undefined, search: params.search,
        customer_name: params.customer_name, mobile: params.mobile, so_number: params.so_number,
        start_date: params.start_date, end_date: params.end_date, location_id: params.location_id,
      })}`,
    ) as Promise<SalesOrderListResponse>,

  /** Every order the list filters match as CSV — written by the API from all pages (T27). */
  exportCsv: (params: Omit<SalesOrderListParams, 'page' | 'limit'>) =>
    apiBlob(`/sales-orders/export${exportQuery(params as Record<string, string | undefined>)}`),

  stats: (params: SalesOrderStatsParams) =>
    withFallback(
      () => apiClient<SalesOrderStats>(`/sales-orders/stats${buildQuery({ location_id: params.location_id })}`),
      EMPTY_STATS,
    ),

  getById: (id: string) => withFallback(() => apiClient<SalesOrder>(`/sales-orders/${id}`), null),

  downloadProforma: (id: string) => apiBlob(`/sales-orders/${id}/proforma`),

  createDelivery: (id: string, payload: CreateDeliveryPayload) =>
    apiClient<SalesOrderDelivery>(`/sales-orders/${id}/deliveries`, { method: 'POST', body: payload }),

  createInvoice: (id: string, payload: CreateInvoicePayload) =>
    apiClient<SalesOrderInvoice>(`/sales-orders/${id}/invoices`, { method: 'POST', body: payload }),

  cancelInvoice: (id: string, invoiceId: string) =>
    apiClient<SalesOrder>(`/sales-orders/${id}/invoices/${invoiceId}/cancel`, { method: 'POST', body: {} }),

  downloadInvoicePdf: (id: string, invoiceId: string) => apiBlob(`/sales-orders/${id}/invoices/${invoiceId}/pdf`),

  recordPayment: (id: string, payload: RecordSalesPaymentPayload) =>
    apiClient<SalesOrderPayment>(`/sales-orders/${id}/payments`, { method: 'POST', body: payload }),

  addDocument: (id: string, payload: SalesOrderDocumentPayload) =>
    apiClient<SalesOrder>(`/sales-orders/${id}/documents`, { method: 'POST', body: payload }),

  deleteDocument: (id: string, docId: string) =>
    apiClient<{ deleted: boolean }>(`/sales-orders/${id}/documents/${docId}`, { method: 'DELETE' }),

  cancel: (id: string, payload: CancelSalesOrderPayload) =>
    apiClient<SalesOrder>(`/sales-orders/${id}/cancel`, { method: 'POST', body: payload }),
};
